<template>
  <div>
    <o-description-details :term="$t('general.subtotal')" align-right term-col="sm:col-span-2" definition-col="sm:col-span-4" padding="py-1">
      <span class="font-medium">{{ subtotal }}</span>
    </o-description-details>

    <o-description-details
      v-if="hasCharges"
      :term="$t('order.charges')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="py-1"
    >
      <span class="font-medium">{{ charges }}</span>
    </o-description-details>

    <!--<o-description-details
      term="GST"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="py-1"
    >
      <span class="font-medium">{{ taxes }}</span>
    </o-description-details>-->

    <o-description-details
      v-for="tax in order.data.taxTotals"
      :key="tax.tax.idTax"
      :term="tax.tax.name"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="py-1"
    >
      <span class="font-medium">{{ $currency.format(tax.total) }}</span>
    </o-description-details>

    <o-description-details
      :term="$t('general.total')"
      align-right
      class="border-t dark:border-gray-800 mt-2 pt-2"
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="py-1"
    >
      <p class="font-medium">
        {{ total }}
      </p>
    </o-description-details>

    <o-description-details
      v-if="discounts"
      :term="$t('general.discounts')"
      align-right
      class="border-t dark:border-gray-800 mt-2 pt-2"
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="py-1"
    >
      <p class="font-medium">
        {{ discounts }}
      </p>
    </o-description-details>

    <o-description-details
      :term="$t('order.tip')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="py-1"
    >
      <template #term>
        <div class="flex items-center space-x-1 leading-none h-full">
          <p>{{ $t('order.tip') }}</p>
          <o-tooltip v-if="!isClosed" :text="$t('order.editTip')">
            <o-button outline flat size="xs" icon="edit" @click="handleEditTip()">
              {{ $t('order.editTip') }}
            </o-button>
          </o-tooltip>
        </div>
      </template>
      <span class="font-medium">{{ tip }}</span>
    </o-description-details>

    <o-description-details
      :term="$t('general.payments')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="py-1"
    >
      <span class="font-medium">{{ payments }}</span>
    </o-description-details>

    <o-description-details
      :term="$t('order.remainder')"
      align-right
      class="border-t dark:border-gray-800 mt-3 pt-2"
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="py-1"
    >
      <span class="font-medium text-lg">{{ remainder }}</span>
    </o-description-details>

    <o-description-details
      v-if="$features.get('loyalty') && hasLoyalty"
      :term="$t('components.order.edit.totals.loyaltyPointsEarned')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      padding="pt-3"
      class="border-t dark:border-gray-800 mt-2 pt-2"
    >
      <span class="font-medium">{{ loyalty }}</span>
    </o-description-details>
  </div>
</template>

<script setup lang="ts">
import {
  ODescriptionDetails,
} from '@/components/elements'

const { $currency } = useNuxtApp()

interface Props {
  order: any
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'editTip'): void
}>()

const currencySymbol = computed(() => props.order.data.currencySymbol)
const subtotal = computed(() => $currency.format(props.order.data.totals.subtotal))
const charges = computed(() => $currency.format(props.order.data.totals.charges))
const taxes = computed(() => $currency.format(props.order.data.totals.taxes))
const total = computed(() => $currency.format((props.order.data.totals.total - Number(props.order.data.totals.tip || 0))))
const tip = computed(() => $currency.format(Number(props.order.data.totals.tip)))
const payments = computed(() => $currency.format(props.order.data.totals.payments))
const remainder = computed(() => $currency.format(props.order.data.totals.remainder))
const loyalty = computed(() => props.order.data.totals.loyaltyPointsEarned)

const discounts = computed(() => {
  const d = props.order.data.totals.discounts
  return d ? $currency.format(d) : null
})

const hasCharges = computed(() => props.order.data.items.charges.length)
const hasRemaining = computed(() => props.order.hasRemaining)
const hasLoyalty = computed(() => props.order.data.totals.loyaltyPointsEarned)
const isClosed = computed(() => props.order.closed)

function handleEditTip() {
  emit('editTip')
}
</script>
